/**
 * Shell dock loader for /dock/shell[/<pointer>].
 *
 * Two layers:
 *   - `loadShell(shellId)` — pure primitive. Resolves a plain Shell's identity
 *     and project and writes dataContext. Throws typed errors. No redirects.
 *   - `loadShellRoute(pointer)` — route wrapper. Dispatches on pointer shape,
 *     delegates to `loadShell` / `loadProcess`. On any per-candidate failure,
 *     hands off to `loadNextProcess` to find the next-best tab.
 *
 * Dispatch shapes:
 *   - no pointer          → loadNextProcess() — pick & load the best candidate
 *   - "new_terminal"      → create a fresh Shell and redirect into it
 *   - agentic_process-*   → loadProcess(); on typed failure → loadNextProcess
 *   - shell-<uuid> / uuid → loadShell();   on typed failure → loadNextProcess
 *                           (unless a process owns it — redirect to process)
 *
 * Failure UI: 1 cleanup → toast; 2+ cleanups → modal counter.
 */

import type { ShowTarget } from '@sdk';
import { t } from '@lingui/core/macro';
import {
  AgenticProcess,
  ContextEntitiesEnum,
  dataContext,
  dataManager,
  QueryRequest,
  Project,
  Shell,
  ShellStatus,
  systemTools,
  tabManager,
  toplog,
  TypeId,
} from '@sdk';
import { sinceTabSwitch } from '@src/navigation/tab-switch-state';
import { notify } from '@src/notifications';
import { buildShellRedirectUrl, detectLayout, DockPointer } from '@src/navigation';
import { sessionOpenViewMode, ViewMode } from '@src/contexts/view-mode-context';
import { NODE_PARAM } from '@src/navigation/DockPointer';
import { isHubOnly } from '@src/navigation/hub-runtime';
import { activeDisplayDock } from '@src/navigation/open-active-display';

/**
 * The URL-derived values that must survive the scope-align redirect in
 * `reconcileProcessScope`. `options` is the complete parsed query record: scope
 * reconciliation owns only the scope keys and must preserve every unrelated
 * option (view mode, side windows, deep-link metadata, and future additions).
 */
export interface ProcessRouteCarry {
  scope?: ScopeFilter | null;
  viewMode?: ViewMode | null;
  options?: Record<string, string>;
  /** The RESOLVE phase already ran for this navigation (the dock loader runs it at
   *  step 3, before a tab is minted). Resolving again re-reads the process and, for a
   *  projectless one, re-resolves its project path — inside the warm visit's budget. */
  resolved?: boolean;
}

/** What a shell route carries through a redirect, read off its dock. */
export function processRouteCarry(dock: DockPointer, extra: Partial<ProcessRouteCarry> = {}): ProcessRouteCarry {
  return { scope: dock.scopeFilter, viewMode: dock.viewMode, options: dock.options, ...extra };
}
import { ViewType } from '@sdk';
import { projectScope, scopeFilterEqual, type ScopeFilter } from '@src/lib/scope-filter';
import { redirect } from 'react-router';
import { errorStatus } from '@src/lib/error-message';
import { perfLog, perfTime } from './_perf';
import { loadProcess, ProcessLoadError, resolveProcessIdentity } from './load-process';
import { loadProject } from './load-project';
import {
  buildProcessCleanup,
  buildShellCleanup,
  loadNextProcess,
  type CleanupRecord,
  type LoadedNext,
} from './load-next-process';

// ── typed error (for plain-Shell loads) ─────────────────────────────────────

export class ShellLoadError extends Error {
  constructor(
    readonly kind: 'not_found' | 'error_status',
    readonly shellId: string,
    readonly errorMessage?: string | null,
    readonly cause?: unknown,
  ) {
    super(`shell-load:${kind}`);
  }
}

// ── internal helpers ────────────────────────────────────────────────────────

/** A plain-shell pointer is `shell-<id>` or the bare id. */
function shellIdFromPointer(pointer: string): string {
  return pointer.startsWith(Shell.type + '-') ? pointer.slice(Shell.type.length + 1) : pointer;
}

// ── CORE: loadShell(shellId) — pure, no redirects ───────────────────────────

/**
 * Load a plain Shell by id: cache-first fetch, resolve its project, write
 * dataContext. Identity only — the PTY attach (`shell.start`) belongs to the
 * mounted terminal panel, exactly as `loadProcess` leaves `process.start` to it
 * (docs/navigation/dock-loading.md, I3): a loader that attaches holds the URL on
 * a WS round trip, and repeated it on every switch back to the tab.
 * Throws ShellLoadError. Never redirects. Does NOT check for a linked
 * AgenticProcess — the route wrapper is responsible for that dispatch.
 */
export async function loadShell(shellId: string): Promise<Shell> {
  const cached = Shell.getByIdFromCache<Shell>(shellId);
  perfLog(`loadShell cache=${cached ? 'hit' : 'miss'} shellId=${shellId.slice(0, 8)}`);
  const shell =
    cached ?? (await perfTime('Shell.getById (network)', () => Shell.getById<Shell>(shellId).catch(() => null)));
  if (!shell) {
    throw new ShellLoadError('not_found', shellId);
  }
  if (shell.status === ShellStatus.ERROR) {
    throw new ShellLoadError('error_status', shellId, shell.error_message ?? null);
  }

  // ── Project phase — URL-first: resolve project into context before the
  // route commits, so the mounted panel's attach reads the right project.
  if (shell.project_id) {
    await loadProject(new TypeId(Project.type, shell.project_id)).catch(() => {
      // Dangling project_id — fall through to workdir-based resolve below.
      return systemTools.resolveProjectContext(shell.workdir ?? undefined, shell);
    });
  } else {
    // A workdir inside a project mount adopts the shell into it; otherwise this
    // is a genuinely global shell and resolveProjectContext clears the active
    // project to null (the Global scope).
    await systemTools.resolveProjectContext(shell.workdir ?? undefined, shell);
  }

  dataContext.setActiveShellId(shell.id);
  dataContext.setActiveTerminalTargetTypeId(shell.typeId);
  // Fire-and-forget server stamp (Part 3 §4 D-A): never awaited — loaders
  // must stay fast; the in-cache bump above is the synchronous seed.
  void shell.activate().catch(() => {});
  // Stamp recency on the Tab too — the close-resolver reads Tab.last_active_at,
  // not the Shell row, so without this close-to-most-recently-active falls back
  // to tab_order.
  tabManager.stampTargetRecency(Shell.type, shell.id);
  dataContext.setWorkdir(shell.workdir ?? dataContext.project?.fs_storage_mount_path ?? null);
  await dataContext.setContextEntityTypeId(ContextEntitiesEnum.CurrentProcessTypeId, null);
  return shell;
}

// Default-tab resolution lives in the SDK TabManager's `resolveNext` facade:
// the single `resolveActive` resolver applied to the pre-filtered tab list,
// retiring `resolveDefaultTab` (tab-management.md Part 1 §5, Phase 3).

// ── cleanup UI dispatch ─────────────────────────────────────────────────────

/**
 * Tell the user what was cleaned up — always a notification, never a dialog.
 * One cleanup speaks for itself; several share one notification that lists
 * each session and why it could not be restored.
 */
function handleCleanups(cleaned: CleanupRecord[]): void {
  if (cleaned.length === 0) return;
  if (cleaned.length === 1) {
    const c = cleaned[0];
    notify.error({ title: c.title, message: c.description });
    return;
  }
  const count = cleaned.length;
  const details = cleaned.map((c) => `• ${c.description ? `${c.title}: ${c.description}` : c.title}`);
  notify.error({
    title: t`Cleaned invalid sessions`,
    message: [t`We skipped ${count} sessions that couldn't be restored.`, ...details].join('\n'),
  });
}

function loadedToPointer(loaded: LoadedNext): string {
  return loaded.kind === 'process' ? loaded.process.terminalDockPointer.pointer : loaded.shell.dockPointer.pointer;
}

// ── ROUTE: internal branches ────────────────────────────────────────────────

/**
 * Layout-preserving shell URL builder (Part 3 §7): redirects issued while
 * loading a `/win/shell` focus window must stay in the win/ layout — a
 * fallback redirect must not dump the window back into full-app chrome.
 * `loadShellRoute` builds one from the request path and threads it down.
 */
type ShellUrlBuilder = (pointer?: string) => string;

async function routeNewTerminal(shellUrl: ShellUrlBuilder): Promise<never> {
  const cn = dataContext.computeNode;
  if (!cn) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(shellUrl());
  }
  const shells = await Shell.query<Shell>(new QueryRequest({ type: Shell.type, scope: [] }));
  const { nextTerminalName } = await import('@src/components/terminal/rename-rules');
  const name = nextTerminalName(shells.map((s) => ({ name: s.name ?? '' })));
  const cwd = dataContext.project?.fs_storage_mount_path ?? undefined;
  const newShell = Shell.create(cn, { name, workdir: cwd });
  await newShell.save(cn.typeId);
  // The new shell's URL takes the place of `new_terminal` (I7), so Back never
  // lands on `new_terminal` and creates another terminal.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect(shellUrl(newShell.dockPointer.pointer));
}

async function routeDefaultShell(shellUrl: ShellUrlBuilder): Promise<void> {
  const result = await loadNextProcess({ projectId: dataContext.project?.id ?? null });
  handleCleanups(result.cleaned);
  if (!result.loaded) {
    // Empty state: clear context, render whatever the shell view shows when
    // nothing is selected.
    dataContext.setActiveShellId('');
    dataContext.setActiveTerminalTargetTypeId(null);
    dataContext.setWorkdir(dataContext.project?.fs_storage_mount_path ?? null);
    await dataContext.setContextEntityTypeId(ContextEntitiesEnum.CurrentProcessTypeId, null);
    return;
  }
  perfLog(`routeDefaultShell redirect → ${shellUrl(loadedToPointer(result.loaded))}`);
  // Bare /dock/shell is a placeholder the user never sees: the concrete shell
  // URL takes its place (I7), so it never becomes a history entry.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect(shellUrl(loadedToPointer(result.loaded)));
}

/**
 * URL-scope ⇐ opened-entity SSOT (the chats-side-menu-wrong-project fix).
 *
 * The opened AgenticProcess's `project_id` is the single source of truth for
 * "what project are we in": it's the project `loadProcess` loads into context.
 * The dock's scope filter (what the Chats navigator reads to list history) must
 * be a PROJECTION of that same id — not the project that happened to be ambient
 * when the scope was seeded at click (`openDock`, NavigationActions). When they
 * diverge (open an oss chat while acme-streams was active → URL carries
 * acme's scope), redirect the URL onto the same pointer carrying the
 * process's own project scope. This runs at LOAD, so it also corrects deep
 * links / hard refresh / back-forward, not just the click path. A genuinely
 * projectless target actively removes any seeded project scope so its tab and
 * the mounted view agree on the Global owner.
 *
 * Keyed off entity IDENTITY only and run BEFORE the runtime phase, so the scope
 * follows `project_id` regardless of how `loadProcess` later resolves — PTY
 * soft-failure, a stale-instance unexpected throw, or success all land on the
 * correctly-scoped URL (the runtime then attaches on the redirected re-run).
 */
async function reconcileProcessScope(processId: string, requestPath: string, carry?: ProcessRouteCarry): Promise<void> {
  // Resolve identity only (cache-first; a cheap get-by-id on cold nav).
  const proc =
    AgenticProcess.getByIdFromCache<AgenticProcess>(processId) ??
    (await AgenticProcess.getById<AgenticProcess>(processId).catch(() => null));
  if (!proc) return;
  const pathProject = !proc.project_id && proc.workdir ? await Project.getProjectByPath(proc.workdir) : null;
  const ownerProjectId = proc.project_id ?? pathProject?.id ?? null;
  // The mode rides the same identity redirect, so the route takes ONE redirect
  // before a tab is minted (I2): a bare URL states the session's remembered
  // mode, else the last mode the user selected.
  // Restoring writes nothing; memory is minted by `VIEW_MODE_STORE`.
  const want = ownerProjectId ? projectScope(ownerProjectId) : null;
  const aligned = want ? !!carry?.scope && scopeFilterEqual(carry.scope, want) : !carry?.scope;
  if (aligned && carry?.viewMode) return; // already aligned and stated — no redirect loop
  const base = new DockPointer(
    ViewType.SHELL,
    proc.terminalDockPointer.pointer,
    carry?.options,
    detectLayout(requestPath),
  ).withViewMode(carry?.viewMode ?? sessionOpenViewMode(proc));
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect((want ? base.withScopeFilter(want) : base.withoutScopeFilter()).toUrl(requestPath));
}

/**
 * Redirect a cold landing on an explicit vibe process URL to `last_shown`,
 * unconditionally, every time — no once-per-session memory.
 * `NavigationActions.openDock`'s same-URL no-op check prevents looping.
 */
export function restoreDisplayRedirect(processId: string, requestPath: string, carry?: ProcessRouteCarry): string | null {
  if (carry?.viewMode !== ViewMode.Vibe) return null;

  const process = AgenticProcess.getByIdFromCache<AgenticProcess>(processId);
  const lastShown = (process?.context_data as { last_shown?: ShowTarget } | undefined)?.last_shown;

  // ONE definition of what the active display can address, shared with the live
  // show path. Restore must refuse exactly what `openActiveDisplay` refuses — a
  // terminal (hosted as its own child tab), a bare port (following it walks the
  // user out of the workspace to a chrome-less web-app dock), and anything with no
  // dock at all — and sharing the builder is what makes that true by construction
  // rather than by a comment asking the next person to keep two lists in step.
  const placed = activeDisplayDock(lastShown, {
    host: `${AgenticProcess.type}-${processId}`,
    projectId: process?.project_id ?? null,
  });
  if (!placed) return null;

  return placed.toUrl(requestPath);
}

/**
 * A process URL that is dead (entity gone, or unreachable): fall back to the next
 * candidate, which takes the dead URL's place (I7). Never returns.
 */
async function fallbackFromDeadProcess(processId: string, e: ProcessLoadError, shellUrl: ShellUrlBuilder): Promise<never> {
  const directCleanup = buildProcessCleanup(e);
  toplog.log(
    'tab_switch',
    `error ${sinceTabSwitch()} sink=process_missing kind=${e.kind} proc=${processId.slice(0, 8)} → fallback`,
  );
  const next = await loadNextProcess({
    excludeIds: new Set([processId]),
    projectId: dataContext.project?.id ?? null,
  });
  handleCleanups([directCleanup, ...next.cleaned]);
  if (!next.loaded) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(shellUrl());
  }
  const fallbackPointer = loadedToPointer(next.loaded);
  const requestedProc = AgenticProcess.getByIdFromCache<AgenticProcess>(processId);
  const requestedName = requestedProc?.name ?? requestedProc?.displayName ?? `${processId.slice(0, 8)}…`;
  const fallbackName =
    next.loaded.kind === 'process'
      ? (next.loaded.process.name ?? next.loaded.process.displayName ?? fallbackPointer)
      : (next.loaded.shell.name ?? fallbackPointer);
  notify.error({
    title: t`Terminal "${requestedName}" not found`,
    message: t`${directCleanup.title} — opened "${fallbackName}" instead.`,
  });
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect(shellUrl(fallbackPointer));
}

async function routeProcessPointer(
  processId: string,
  shellUrl: ShellUrlBuilder,
  requestPath: string,
  carry?: ProcessRouteCarry,
): Promise<void> {
  // Scope alignment is the RESOLVE phase's (`resolveShellRoute`), which ran before
  // this one and threw its redirect if the URL diverged from the process's
  // project. By here the scopes match, so the runtime attaches under the right one.
  try {
    await loadProcess(processId);
    // The URL states its mode by here — `reconcileProcessScope` redirected a bare
    // one onto the session's remembered mode before any tab was minted.
    // Successful load — clear any prior runtime-error banner.
    dataContext.setTerminalRuntimeError(null);
    // Restore AFTER the load: the process is in cache, and the scope has already
    // been aligned above, so this only ever fires from a URL `reconcileProcessScope`
    // has declared canonical — the two redirects can never ping-pong.
    const displayUrl = restoreDisplayRedirect(processId, requestPath, carry);
    if (displayUrl) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect(displayUrl);
    }
    return;
  } catch (e) {
    if (!(e instanceof ProcessLoadError)) throw e;

    // Soft failure — the entity exists, only the runtime is broken (PTY
    // died, shell entity missing, project dangling, …). DO NOT redirect:
    // keep the user on their requested URL and surface a banner with
    // per-kind recovery. This is what prevents the silent-jump-to-stale-
    // sibling class of bugs after a backend restart. (Scope is already
    // aligned above, before this phase ran.)
    if (e.severity === 'soft') {
      toplog.log(
        'tab_switch',
        `error ${sinceTabSwitch()} sink=process_runtime_soft kind=${e.kind} proc=${processId.slice(0, 8)}`,
      );
      dataContext.setActiveTerminalTargetTypeId(new TypeId(AgenticProcess.type, processId));
      dataContext.setTerminalRuntimeError({
        kind: e.kind as Exclude<typeof e.kind, 'entity_not_found'>,
        processId,
        shellId: e.shellId ?? null,
      });
      // No toast — ``TerminalRuntimeErrorBanner`` (mounted in
      // InteractiveTerminal) reads the dataContext slot and renders a
      // persistent banner with the recovery action. A toast on top of
      // that would be redundant.
      return;
    }

    // Hard failure — the URL itself is dead.
    await fallbackFromDeadProcess(processId, e, shellUrl);
  }
}

async function routePlainShellPointer(pointer: string, shellUrl: ShellUrlBuilder): Promise<void> {
  const shellId = shellIdFromPointer(pointer);

  // A shell someone's process owns never reaches here: the RESOLVE phase
  // (`resolveShellRoute`) threw the redirect to the process URL, warm or cold.
  // What is left is a shell of its own.
  try {
    await loadShell(shellId);
    return;
  } catch (e) {
    if (!(e instanceof ShellLoadError)) throw e;

    // Pointer wasn't a Shell id — try resolving it as a Claude/Codex worker
    // session id via the backend before falling back to "next process". This
    // is the URL-deep-link path: /dock/shell/<worker-session-uuid>.
    if (e.kind === 'not_found') {
      const recovered = await AgenticProcess.getByWorkerId(shellId).catch(() => null);
      if (recovered) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect(shellUrl(recovered.terminalDockPointer.pointer));
      }
    }

    const directCleanup = buildShellCleanup(e);
    toplog.log('tab_switch', `error ${sinceTabSwitch()} sink=shell_missing kind=${e.kind} shell=${shellId.slice(0, 8)} → fallback`);
    const next = await loadNextProcess({
      excludeIds: new Set([shellId]),
      projectId: dataContext.project?.id ?? null,
    });
    handleCleanups([directCleanup, ...next.cleaned]);
    if (!next.loaded) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect(shellUrl());
    }
    const fallbackPointer = loadedToPointer(next.loaded);
    notify.error({
      title: t`Opened a different terminal`,
      message: t`Couldn't load ${shellId.slice(0, 8)}… (${directCleanup.title}) — opened ${fallbackPointer} instead.`,
    });
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(shellUrl(fallbackPointer));
  }
}

// ── RESOLVE: identity-only redirects, before anything is written ────────────


/**
 * Step 3 of docs/navigation/dock-loading.md for shell URLs: every redirect a shell
 * URL takes on IDENTITY alone — the owning project's scope, a dead process, a
 * plain shell a process owns — decided before the route materializes a tab or
 * writes context (I2). Throws a redirect, or returns when the URL is
 * already where it should be. Read-only apart from the dead-link fallback, which
 * resolves the sibling to go to.
 */
export async function resolveShellRoute(
  pointer: string | undefined,
  requestPath: string = '/dock/shell',
  carry?: ProcessRouteCarry,
): Promise<void> {
  const shellUrl: ShellUrlBuilder = (p?: string) => buildShellRedirectUrl(requestPath, p, carry?.options);
  if (pointer && DockPointer.isAgenticProcessPointer(pointer)) {
    const processId = DockPointer.extractAgenticProcessId(pointer);
    const identity = await resolveProcessIdentity(processId).catch((e: unknown) => {
      if (e instanceof ProcessLoadError) return e;
      throw e;
    });
    // Only a URL that is certainly dead falls back; a network blip keeps the user
    // where they asked to be (the load phase surfaces it with a Retry).
    if (identity instanceof ProcessLoadError) {
      if (identity.kind === 'entity_not_found') await fallbackFromDeadProcess(processId, identity, shellUrl);
      return;
    }
    await reconcileProcessScope(processId, requestPath, carry);
    return;
  }
  if (!pointer || pointer === 'new_terminal' || isHubOnly()) return;
  const shellId = shellIdFromPointer(pointer);
  const linkedProcess = dataManager.findInCache<AgenticProcess>((p) => p.shell_id === shellId, AgenticProcess.type);
  const ownerId =
    linkedProcess?.id ??
    (Shell.getByIdFromCache<Shell>(shellId) ?? (await Shell.getById<Shell>(shellId).catch(() => null)))
      ?.agentic_process_id;
  if (ownerId) {
    // A process owns this shell: its URL is the process's — in ONE hop, already
    // scoped to the process's project (a bare process URL would redirect again).
    // Scope alignment throws that URL; a projectless owner falls through to the
    // bare one. The process URL takes the shell URL's place (I7).
    await reconcileProcessScope(ownerId, requestPath, { ...carry, scope: undefined });
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect(shellUrl(new TypeId(AgenticProcess.type, ownerId).toString()));
  }
}

// ── HUB: a terminal on a remote compute node ────────────────────────────────

/**
 * The hub keeps no Shell rows — a terminal there is a PTY session on a compute
 * node (`terminal-command/*`), named by the client-chosen shell id. So the URL is
 * the whole identity: the shell id plus `?node=<compute node id>`, and a cold
 * load rebuilds the same in-memory Shell the click made instead of fetching one.
 */
function loadHubShell(shellId: string, nodeId: string | undefined): Shell {
  const shell =
    Shell.getByIdFromCache<Shell>(shellId) ??
    (nodeId ? Shell.adopt({ id: shellId, name: null, compute_node_id: nodeId }) : null);
  if (!shell) throw new ShellLoadError('not_found', shellId);
  dataContext.setActiveShellId(shell.id);
  dataContext.setActiveTerminalTargetTypeId(shell.typeId);
  return shell;
}

// ── ROUTE: public entry point ───────────────────────────────────────────────

/**
 * Route-level loader for /dock/shell. Registered from main-loader.ts.
 *
 * Owns the redirect policy for this URL namespace. Internals delegate to the
 * pure `loadShell` / `loadProcess` primitives, with `loadNextProcess` as the
 * single recovery / fallback primitive.
 */
export async function loadShellRoute(
  pointer: string | undefined,
  requestPath: string = '/dock/shell',
  carry?: ProcessRouteCarry,
): Promise<void> {
  perfLog(`loadShellRoute(${pointer || 'no-pointer'}) start`);

  // All redirects below preserve the request's layout keyword (dock/dev/win)
  // so a /win/shell focus window never falls back into full-app chrome (§7).
  const shellUrl: ShellUrlBuilder = (p?: string) => buildShellRedirectUrl(requestPath, p, carry?.options);

  // Resolve first — unless the caller already did (the dock loader runs it at step 3,
  // before materializing the tab). A direct caller gets the same redirect policy.
  if (!carry?.resolved) await resolveShellRoute(pointer, requestPath, carry);

  if (isHubOnly() && pointer && pointer !== 'new_terminal') {
    loadHubShell(shellIdFromPointer(pointer), carry?.options?.[NODE_PARAM]);
    return;
  }

  // Every shell URL resolves identity/context only. The mounted TerminalPanel
  // owns the WS-bound start/attach (process and plain shell alike), so nothing
  // here waits on realtime readiness before React Router can commit the URL.
  if (pointer && DockPointer.isAgenticProcessPointer(pointer)) {
    const processId = DockPointer.extractAgenticProcessId(pointer);
    await routeProcessPointer(processId, shellUrl, requestPath, carry);
    perfLog('loadShellRoute done (agentic process path)');
    return;
  }

  if (pointer === 'new_terminal') {
    await routeNewTerminal(shellUrl);
  }

  if (!pointer) {
    await routeDefaultShell(shellUrl);
    return;
  }

  await routePlainShellPointer(pointer, shellUrl);
  perfLog('loadShellRoute done (shell path)');
}
